import { FC, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import MoveDownIcon from "@mui/icons-material/MoveDownOutlined";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { useFetchAbsenceGroups } from "src/Pages/Company/Absences/api/absenceGroups.queries";
import useBasePath from "src/utils/hooks/useBasePath";
import filterAbsenceGroupData from "src/utils/Company/Absences/filterAbsenceGroupData";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import AbsenceGroupsList from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsList";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import useAbsenceGroupSortingModal from "./AbsenceGroupSorting/useAbsenceGroupSortingModal";

const AbsenceGroups: FC = () => {
  const match = useRouteMatch();
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const {
    data: absenceGroupsData,
    isFetching,
    isError,
  } = useFetchAbsenceGroups(companyId);

  const { data: absenceTypesData } = useListAbsenceTypes(companyId);

  const { openSortingModal } = useAbsenceGroupSortingModal({
    companyId,
    absenceGroups: absenceGroupsData,
    absenceTypes: absenceTypesData,
  });

  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTab: "active" | "inactive"
  ) => {
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  const [activeGroups, inactiveGroups] =
    filterAbsenceGroupData(absenceGroupsData);

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Grupy nieobecności i statusy obecności"),
          },
        ]}
      />
      <Title
        title={__("Grupy nieobecności i statusy obecności")}
        subtitle={__(
          "Przestrzeń do tworzenia grup nieobecności i statusów obecności. Podczas edycji grupy możesz dodać nazwę, opis, ustawić kolejność wyświetlania oraz przypisać dedykowany kolor, aby wyróżnić grupę."
        )}
      />
      <Stack
        direction="row"
        sx={{ alignItems: "center", mb: "1rem" }}
        spacing={2}
      >
        {canWriteAbsencesConfiguration && (
          <>
            {activeTab === "active" && (
              <Button
                variant="outlined"
                startIcon={<MoveDownIcon />}
                onClick={openSortingModal}
                disabled={!absenceGroupsData}
                data-testid="absence-groups__button-change-sorting"
              >
                {__("Zmień kolejność")}
              </Button>
            )}
            <Button
              component={Link}
              variant="contained"
              to={`${match.url}/new`}
              startIcon={<AddIcon />}
              sx={{ marginBottom: "auto" }}
              data-testid="absence-groups__button-add"
            >
              {__("Nowa grupa")}
            </Button>
          </>
        )}
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={handleTabChange}
          aria-label={__("Wybierz typ grupy")}
          sx={{
            marginLeft: "auto !important",
            "& .MuiToggleButton-root": {
              textTransform: "none",
            },
          }}
        >
          <ToggleButton value="active" aria-label={__("Grupy aktywne")}>
            {__("Aktywne")}
          </ToggleButton>
          <ToggleButton value="inactive" aria-label={__("Grupy nieaktywne")}>
            {__("Nieaktywne")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}
      {isError ? (
        <ErrorDataFetching />
      ) : (
        <>
          <Box sx={{ display: activeTab === "active" ? "block" : "none" }}>
            <AbsenceGroupsList isFetching={isFetching} data={activeGroups} />
          </Box>
          <Box sx={{ display: activeTab === "inactive" ? "block" : "none" }}>
            <AbsenceGroupsList isFetching={isFetching} data={inactiveGroups} />
          </Box>
        </>
      )}
    </>
  );
};

export default AbsenceGroups;
